import { cn } from "@/common/helpers/utils";
import { RowData, flexRender } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { ReactNode } from "react";
import { DataTableHeaderInstance, ImplementedColumnDef } from "../types";
import { RowWithIdentifier } from "../_helpers/row-identifier";

export const wrapHeaderWithSortingComponent = <TData extends RowData & RowWithIdentifier, TValue>(
  header: DataTableHeaderInstance<TData, TValue>,
): ReactNode => {
  const column = header.column;
  const sortDirection = column.getIsSorted();
  const canSort = column.getCanSort();
  const align = (column.columnDef as ImplementedColumnDef<TData, TValue>).align || "left";

  const alignmentClass = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  }[align];

  if (!canSort) {
    return (
      <div className={cn("flex items-center", alignmentClass)}>
        {flexRender(column.columnDef.header, header.getContext())}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex cursor-pointer items-center select-none",
        "hover:text-foreground -mx-2 -my-1 rounded-md px-2 py-1 transition-all duration-200",
        "hover:bg-muted/40",
        alignmentClass,
      )}
      onClick={(e) => {
        e.stopPropagation();
        column.toggleSorting(sortDirection === "asc");
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          column.toggleSorting(sortDirection === "asc");
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Sort by ${column.id}`}
    >
      {flexRender(column.columnDef.header, header.getContext())}
      <span className="ml-2 shrink-0 opacity-50 transition-all duration-200 group-hover:opacity-100">
        {sortDirection === "asc" ? (
          <ChevronUpIcon className="text-primary h-4 w-4 transition-transform group-hover:scale-110" />
        ) : sortDirection === "desc" ? (
          <ChevronDownIcon className="text-primary h-4 w-4 transition-transform group-hover:scale-110" />
        ) : (
          <div className="flex h-4 w-4 flex-col items-center justify-center opacity-30 transition-opacity group-hover:opacity-60">
            <ChevronUpIcon className="-mb-0.5 h-2 w-2" />
            <ChevronDownIcon className="h-2 w-2" />
          </div>
        )}
      </span>
    </div>
  );
};
